// shared/products.ts
// 7 个产品的共享定义（前端展示与后端签发共用）。
// 对齐来源：license_generators（unified_panel.rs 的 Software 枚举与前缀映射）。
//
// 授权模式（2026-10-19 起，七款软件一致）：
//   免费版 = 装好即用，内置演奏者/角色可直接跑完从 midi 到动画的完整流程，不需要授权码；
//   完整版 = 购买授权码后在软件里激活，可以自定义角色，并开启更多功能。
// 所以「哪些产品免费」这个问题已经不存在：七款都是免费版可用 + 完整版可选。

// ===== 定价：全站唯一来源 =====
//
// 网页上凡是出现「完整版价格」的地方都从这里取，不要在别处再写死一个数字：
//   - 购买页 /buy.html 优先显示后端 /api/products 下发的价格（线上库为准），
//     后端不可用时降级用这里的常量（见 web/src/buy.ts）；
//   - 免费版下载页 /free/download.html 由 web/src/free.ts 读取这里的常量渲染。
//
// 改价的完整流程（三处必须一致）：
//   1) 改下面这两个常量；
//   2) 同步 workers/schema.sql 与 workers/src/db/schema.ts 里的 products 种子
//      （新部署的库靠种子建表，但 INSERT OR IGNORE 不会更新已存在的行）；
//   3) 线上库执行一条 UPDATE（价格不会自己同步过去）：
//      UPDATE products SET price_cny = 9800, price_usd = 4000;
//
// 单位：人民币用「分」，美元用「美分」（9800 = ¥98.00，4000 = $40.00）。
export const FULL_VERSION_PRICE_CNY = 9800;
export const FULL_VERSION_PRICE_USD = 4000;

/**
 * 币种跟着页面语言走：中文页只谈人民币、英文页只谈美元。
 * 两边的定价政策不同（境内走人民币渠道、海外走美元渠道），所以任何一种语言都不
 * 把另一种货币摆出来，免得用户按另一个价格预期付款。
 */
export type PriceLang = 'zh' | 'en';

/** 把「分」/「美分」写成金额：整数就不带小数，否则保留两位 */
export function formatMinorUnits(amountMinor: number): string {
  const wholeAmount = amountMinor / 100;
  return Number.isInteger(wholeAmount)
    ? String(wholeAmount)
    : wholeAmount.toFixed(2);
}

/** 「¥98 / 份」（中文）或「$40 USD / app」（英文）这样的展示串 */
export function formatFullVersionPrice(lang: PriceLang): string {
  return lang === 'zh'
    ? `¥${formatMinorUnits(FULL_VERSION_PRICE_CNY)} / 份`
    : `$${formatMinorUnits(FULL_VERSION_PRICE_USD)} USD / app`;
}

export interface Product {
  /** 唯一键（数据库 product_key） */
  key: string;
  /** 授权码前缀（v2 签名消息与授权码格式用） */
  prefix: string;
  /** 中文名（待作者确认） */
  nameZh: string;
  /** 英文名 */
  nameEn: string;
  emoji: string;
  /** 完整版价格（人民币，单位：分） */
  priceCny: number;
  /** 完整版价格（美元，单位：美分） */
  priceUsd: number;
  /**
   * 免费软件（不售卖、按打赏处理）。
   *
   * 2026-10-19 起**没有任何产品使用这个标记**：七款软件统一改成
   * 「免费版可用 + 完整版付费解锁」，购买页按正常流程卖完整版授权码。
   * 打赏相关的后端分支仍然保留（`orders.kind = 'tip'`），将来若要重新开放打赏，
   * 只需再给某款产品加上 `free: true`。
   */
  free?: boolean;
}

export const PRODUCTS: Product[] = [
  { key: 'string_flow', prefix: 'SF', nameZh: 'String Flow', nameEn: 'String Flow', emoji: '🎻', priceCny: FULL_VERSION_PRICE_CNY, priceUsd: FULL_VERSION_PRICE_USD },
  { key: 'key_ripple',  prefix: 'KR', nameZh: 'Key Ripple',  nameEn: 'Key Ripple',  emoji: '🎹', priceCny: FULL_VERSION_PRICE_CNY, priceUsd: FULL_VERSION_PRICE_USD },
  { key: 'fret_dance',  prefix: 'FD', nameZh: 'Fret Dance',  nameEn: 'Fret Dance',  emoji: '🎸', priceCny: FULL_VERSION_PRICE_CNY, priceUsd: FULL_VERSION_PRICE_USD },
  { key: 'beat_bloom',  prefix: 'BB', nameZh: 'Beat Bloom',  nameEn: 'Beat Bloom',  emoji: '💥', priceCny: FULL_VERSION_PRICE_CNY, priceUsd: FULL_VERSION_PRICE_USD },
  { key: 'zheng_drift', prefix: 'GZ', nameZh: 'Zheng Drift', nameEn: 'Zheng Drift', emoji: '♙', priceCny: FULL_VERSION_PRICE_CNY, priceUsd: FULL_VERSION_PRICE_USD },
  { key: 'harp_glide',  prefix: 'HG', nameZh: 'Harp Glide',  nameEn: 'Harp Glide',  emoji: '🎵', priceCny: FULL_VERSION_PRICE_CNY, priceUsd: FULL_VERSION_PRICE_USD },
  { key: 'wind_rise',   prefix: 'WR', nameZh: 'Wind Rise',   nameEn: 'Wind Rise',   emoji: '🎺', priceCny: FULL_VERSION_PRICE_CNY, priceUsd: FULL_VERSION_PRICE_USD },
];

/** 按 key 查产品；不存在返回 undefined */
export function findProduct(key: string): Product | undefined {
  return PRODUCTS.find((p) => p.key === key);
}

/** 是否免费产品（不售卖；只能打赏 + 免费下载）。2026-10-19 起没有产品命中。 */
export function isFreeProduct(key: string): boolean {
  return PRODUCTS.find((p) => p.key === key)?.free === true;
}

/**
 * 免费版下载页。七款软件共用一个下载页：软件包与预设工程文件都在这里，
 * 中文用户看百度网盘、英文用户看 Google Drive。
 */
export const FREE_DOWNLOAD_PAGE_URL = '/free/download.html';

/** 兼容旧调用：按产品查下载页（七款现在都指向同一个下载页） */
export const FREE_DOWNLOAD_PAGE: Record<string, string> = Object.fromEntries(
  PRODUCTS.map((p) => [p.key, FREE_DOWNLOAD_PAGE_URL]),
);

/** 兼容旧调用：按产品查「免费版获取页」（已并入下载页） */
export const FREE_LANDING_PAGE: Record<string, string> = Object.fromEntries(
  PRODUCTS.map((p) => [p.key, FREE_DOWNLOAD_PAGE_URL]),
);

/** 按前缀查产品 */
export function findProductByPrefix(prefix: string): Product | undefined {
  return PRODUCTS.find((p) => p.prefix === prefix);
}

/** 机器码格式校验（与《网站建设计划》§3.1 一致：大写十六进制 1-16 位） */
export function isValidMachineCode(code: string): boolean {
  return /^[0-9A-F]{1,16}$/.test(code.trim().toUpperCase());
}

/** 机器码归一化：trim + 大写（与 unified_panel.rs 的 trim().to_uppercase() 一致） */
export function normalizeMachineCode(code: string): string {
  return code.trim().toUpperCase();
}
